import {useLayoutEffect, useState} from 'react';

// a grid this short is barely a list, but it still beats one with no rows in view at all
const MIN_HEIGHT = 200;

export interface AvailableHeightOptions {
    /** what the page keeps below the grid: a fixed action bar, the card's own padding */
    bottomGap?: number;
    /**
     * A block above the grid that shows and hides - a warning, a batch panel - moves the grid
     * down without the window resizing. This is the element to watch for that, as a selector
     * `closest()` finds from the grid itself.
     */
    watchSelector?: string;
    /**
     * How short the grid may be squeezed before it is allowed to overflow instead. Worth
     * dropping to 0 where bottomGap stands for something drawn over the page - a fixed action
     * bar - since a grid that overflows past one hides its last rows behind it.
     */
    minHeight?: number;
}

/**
 * The height a grid should fill: whatever is left of the viewport below its own top.
 *
 * An ag-grid virtualises its rows, so it owns its scrolling area and needs a height in pixels -
 * and what sits above it is rarely a fixed height.
 *
 * The element is passed in rather than held in a ref, so that a grid mounted later - once its
 * first page has landed, say - is measured when it appears. Pages hand this a callback ref.
 */
export function useAvailableHeight(element: HTMLElement | null, options: AvailableHeightOptions = {}): number | null {
    const {bottomGap = 0, watchSelector, minHeight = MIN_HEIGHT} = options;
    const [height, setHeight] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (!element) return;

        const measure = () => {
            const top = element.getBoundingClientRect().top;
            setHeight(Math.max(minHeight, window.innerHeight - top - bottomGap));
        };

        measure();
        window.addEventListener('resize', measure);
        const watched = watchSelector ? element.closest(watchSelector) : null;
        const observer = watched && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        if (watched && observer) {
            observer.observe(watched);
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [element, bottomGap, watchSelector, minHeight]);

    return height;
}
